import { Rx, useRegulationText } from '@/lib/regulation-terms';
import { useState, useEffect, useMemo } from 'react';
import { motion } from 'motion/react';
import { Search, GitBranch, Database, Download, ArrowRight, Hash, CheckCircle2, AlertTriangle, ScrollText, Loader2, MapPin, Cloud, Server, FileText, ShieldCheck } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { Progress } from '@/components/ui/primitives';
import { reverseDsar, exportReverseDsar, runDsarErasure, type ReverseDsarResponse, type ReverseDsarExport, type DsarEraseSummary } from '@/lib/api';
import { useCan } from '@/lib/access';
import { deriveLocation } from '@/lib/asset-location';
import { FocttaCoreBadge } from '@/components/foctta-core-badge';

type IdentifierKind = 'email' | 'phone' | 'aadhaar' | 'pan' | 'customer_number';
type DsarSev = 'critical' | 'high' | 'medium' | 'low';

function inferKind(s: string): IdentifierKind {
  if (s.includes('@')) return 'email';
  if (/^\d{12}$/.test(s.replace(/\s+/g, ''))) return 'aadhaar';
  if (/^[A-Z]{5}\d{4}[A-Z]$/i.test(s)) return 'pan';
  if (/^\+?\d{10,15}$/.test(s.replace(/\s+/g, ''))) return 'phone';
  return 'customer_number';
}

/** Severity for a matched asset, inferred from the PII classes present on it. */
function sevForClasses(cls: string[]): DsarSev {
  const s = new Set(cls);
  if (['aadhaar', 'minor_dob', 'credit_card', 'secret_key'].some((l) => s.has(l))) return 'critical';
  if (['pan', 'bank_account', 'ifsc', 'card_last4', 'upi_id'].some((l) => s.has(l))) return 'high';
  if (['email', 'phone', 'phone_in', 'address', 'gstin'].some((l) => s.has(l))) return 'medium';
  return 'low';
}

const SEV_HEX: Record<DsarSev, string> = {
  critical: '#f43f5e',
  high: '#fb923c',
  medium: '#facc15',
  low: '#34d399',
};

/**
 * Compact SVG "spider" graph: the hashed principal on the left, one node per
 * matched asset fanned out on the right, each edge coloured by the asset's
 * severity. Purely presentational (no cytoscape) so it stays light. Clicking a
 * node lifts the matching location card via `onPick`.
 */
function DsarMiniGraph({
  assets,
  activeId,
  onPick,
}: {
  assets: Array<{ assetId: string; displayName: string; sourceType: string; sev: DsarSev }>;
  activeId: string | null;
  onPick: (id: string) => void;
}) {
  const rowH = 34;
  const padY = 24;
  const height = Math.max(180, assets.length * rowH + padY * 2);
  const width = 640;
  const leftX = 90;
  const rightX = 430;
  const centerY = height / 2;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 420 }} role="img" aria-label="subject data search traversal graph">
      {assets.map((a, i) => {
        const y = padY + i * rowH + rowH / 2;
        const midX = (leftX + rightX) / 2;
        const active = activeId === a.assetId;
        return (
          <path
            key={`e-${a.assetId}`}
            d={`M ${leftX + 10} ${centerY} C ${midX} ${centerY}, ${midX} ${y}, ${rightX - 10} ${y}`}
            fill="none"
            stroke={SEV_HEX[a.sev]}
            strokeWidth={active ? 2.6 : 1.4}
            strokeOpacity={active ? 1 : 0.55}
          />
        );
      })}
      <g>
        <circle cx={leftX} cy={centerY} r={26} fill="#0ea5e9" fillOpacity={0.18} stroke="#38bdf8" strokeWidth={2} />
        <text x={leftX} y={centerY - 2} textAnchor="middle" fontSize="9" fill="#e2e8f0" fontWeight="700">DATA</text>
        <text x={leftX} y={centerY + 9} textAnchor="middle" fontSize="9" fill="#e2e8f0" fontWeight="700">SUBJECT</text>
      </g>
      {assets.map((a, i) => {
        const y = padY + i * rowH + rowH / 2;
        const active = activeId === a.assetId;
        return (
          <g key={`n-${a.assetId}`} style={{ cursor: 'pointer' }} onClick={() => onPick(a.assetId)}>
            <circle cx={rightX} cy={y} r={active ? 8 : 6} fill={SEV_HEX[a.sev]} stroke={active ? '#06b6d4' : 'transparent'} strokeWidth={3} />
            <text x={rightX + 14} y={y + 3} fontSize="11" fill={active ? '#e2e8f0' : '#94a3b8'} fontWeight={active ? 700 : 500} fontFamily="ui-monospace, monospace">
              {a.displayName.length > 34 ? a.displayName.slice(0, 33) + '…' : a.displayName}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const STEPS = [
  { id: 1, label: 'Hash identifier', desc: 'SHA-256 of the principal identifier - never travels in plaintext' },
  { id: 2, label: 'Traverse asset graph', desc: 'Column-graph BFS through every linked column' },
  { id: 3, label: 'Resolve findings', desc: 'Match traversal output against existing PII findings' },
  { id: 4, label: 'Export DSAR package', desc: 'Signed JSON + PDF + audit-chain receipt' },
];

// NOTE: a `TRAVERSAL_RESULT` literal lived here in the original cut and
// was never referenced from the render path (the page calls
// `runReverseDsar()` and renders the live response). It was deleted in
// the Phase-2 hardening pass to satisfy the §10 exit criterion
// "No `const SAMPLE_X = [...]` literals at the top of the file."

export function DiscoveryReverseDsar() {
  const tx = useRegulationText();
  const [step, setStep] = useState(0);
  const [identifier, setIdentifier] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<ReverseDsarResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [pickedAsset, setPickedAsset] = useState<string | null>(null);
  const [signedExport, setSignedExport] = useState<ReverseDsarExport | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  async function run(overrideId?: string) {
    const value = (overrideId ?? identifier).trim();
    if (!value) return;
    if (overrideId) setIdentifier(overrideId);
    setRunning(true);
    setProgress(0);
    setStep(1);
    setError(null);
    setResult(null);
    setPickedAsset(null);

    // Visual stepper progresses while we await the real call.
    let p = 0;
    const tick = setInterval(() => {
      p = Math.min(p + 8, 75);
      setProgress(p);
      setStep(Math.min(4, Math.floor(p / 25) + 1));
    }, 150);

    try {
      const kind = inferKind(value);
      const r = await reverseDsar(value, kind);
      setResult(r);
      setProgress(100);
      setStep(4);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'subject data search failed');
    } finally {
      clearInterval(tick);
      setRunning(false);
    }
  }

  // Deep-link auto-run: `?identifier=<value>` lands here from the Lineage
  // inspector's "Find a person's data from here" action and immediately populates
  // the traversal, so the operator sees a result rather than an empty form.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let seed = params.get('identifier') ?? params.get('id');
    // sessionStorage fallback: the router may not serialise an unvalidated
    // search param, so the Lineage "Find a person's data from here" action also
    // stashes the seed here. Read once, then clear so a manual revisit is clean.
    if (!seed) {
      try {
        const stashed = sessionStorage.getItem('dsar_seed');
        if (stashed) {
          seed = stashed;
          sessionStorage.removeItem('dsar_seed');
        }
      } catch {
        /* sessionStorage unavailable - ignore */
      }
    }
    if (seed && seed.trim()) {
      void run(seed.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Location-enriched, severity-ranked view of the matched assets.
  const locatedAssets = useMemo(() => {
    return (result?.assets ?? [])
      .map((a) => {
        const sev = sevForClasses(a.classifications);
        return { ...a, sev, loc: deriveLocation(a.sourceType, a.displayName) };
      })
      .sort((x, y) => {
        const rank: Record<DsarSev, number> = { critical: 0, high: 1, medium: 2, low: 3 };
        return rank[x.sev] - rank[y.sev];
      });
  }, [result]);

  const toxicAssetIds = useMemo(
    () => new Set((result?.toxicCombinations ?? []).map((t) => t.assetId)),
    [result],
  );

  return (
    <PageContainer>
      <PageHeader
        title="Data Subject Search"
        subtitle={tx("Data Subject Access Request - find every PII column belonging to a principal across all connected sources via column-graph traversal.")}
        badges={[{ label: 'Column-graph', variant: 'glass' }, { label: 'DPDPA / GDPR', variant: 'info' }]}
        actions={<FocttaCoreBadge />}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <GlassCard className="p-6 lg:col-span-1">
          <CardHeader className="p-0 mb-4">
            <CardTitle>Initiate request</CardTitle>
            <CardDescription clamp>Hashed identifier in → all related findings out.</CardDescription>
          </CardHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">
                Principal identifier (will be hashed)
              </label>
              <div className="glass-lite rounded-md p-3 flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                <input
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="e.g. customer-uid-1234567 / +91-9876543210 / user@email.com"
                  className="flex-1 bg-transparent outline-none text-sm"
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">
                The identifier is SHA-256 hashed before traversal. Plaintext never leaves your browser.
              </p>
            </div>

            <div className="space-y-2">
              {STEPS.map((s) => (
                <motion.div
                  key={s.id}
                  className="flex items-center gap-3 p-2.5 rounded-md glass-lite"
                  animate={{
                    opacity: step >= s.id ? 1 : 0.4,
                    scale: step === s.id ? 1.02 : 1,
                  }}
                >
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${step > s.id ? 'bg-success text-white' : step === s.id ? 'bg-primary text-white animate-pulse' : 'bg-muted text-muted-foreground'}`}>
                    {step > s.id ? <CheckCircle2 className="h-3 w-3" /> : s.id}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-medium"><Rx>{s.label}</Rx></div>
                    <div className="text-[10px] text-muted-foreground"><Rx>{s.desc}</Rx></div>
                  </div>
                </motion.div>
              ))}
            </div>

            {running && <Progress value={progress} />}

            <Button data-action="disc.dspm_reverse_dsar.a01" onClick={() => void run()} disabled={!identifier || running} className="w-full">
              <Search className="h-4 w-4" />
              {running ? 'Searching the estate...' : "Find this person's data"}
            </Button>
          </div>
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-2">
          <CardHeader className="p-0 mb-4">
            <CardTitle>Traversal result</CardTitle>
            <CardDescription>
              {step >= 4 ? tx('Every PII column linked to the hashed identifier - ready to export') : 'Run a traversal to populate this view'}
            </CardDescription>
          </CardHeader>
          {error && (
            <div className="text-xs px-3 py-2 mb-4 rounded-md bg-red-500/10 border border-red-500/20 text-red-300 font-mono">
              {error}
            </div>
          )}
          {step >= 4 ? (
            <>
              {/*
                FCT-P0-007. Without this the operator cannot tell "we found this
                person here" from "these are assets that could hold an email" -
                the second is the same list for every principal of that kind, and
                reading it as the first is how a <Rx>DSAR</Rx> response becomes wrong.
                Shown for every mode, because a quiet success is also a claim.
              */}
              {result?.resolution && (
                <div
                  className={`mb-4 rounded-md border p-3 ${
                    result.resolution.mode === 'value_matched'
                      ? 'border-emerald-500/30 bg-emerald-500/5'
                      : 'border-amber-500/30 bg-amber-500/5'
                  }`}
                >
                  <div
                    className={`flex items-center gap-2 text-sm font-medium ${
                      result.resolution.mode === 'value_matched'
                        ? 'text-emerald-200'
                        : 'text-amber-200'
                    }`}
                  >
                    {result.resolution.mode === 'value_matched'
                      ? 'Matched on identifier value'
                      : result.resolution.mode === 'no_assets_scanned'
                        ? 'Nothing scanned yet - this is not a "no data" answer'
                        : 'Candidate assets only - not confirmed for this person'}
                    <Badge variant="glass" className="text-[9px] ml-auto">
                      {result.resolution.assetsScanned}/{result.resolution.assetsTotal} assets scanned
                    </Badge>
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                    {result.resolution.detail}
                  </p>
                </div>
              )}
              {result?.focttaCoreEnrichment?.coreIntegrated && (
                <div className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-emerald-200">
                    <ScrollText className="h-4 w-4" />
                    FOCTTA Core cross-reference
                    <Badge variant="glass" className="text-[9px] ml-auto">
                      pin #1 + #6
                    </Badge>
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-emerald-200/80">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider opacity-60">
                        Active consents
                      </div>
                      <div className="text-base font-semibold">
                        {result.focttaCoreEnrichment.consentCount}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-wider opacity-60">
                        Cross-referenced systems (data-map)
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {result.focttaCoreEnrichment.crossRefSystems.slice(0, 8).map((s) => (
                          <code
                            key={s}
                            className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20"
                          >
                            {s}
                          </code>
                        ))}
                        {result.focttaCoreEnrichment.crossRefSystems.length === 0 && (
                          <span className="text-[10px] opacity-60">none on file</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {result.focttaCoreEnrichment.activeConsents.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      {result.focttaCoreEnrichment.activeConsents.slice(0, 10).map((c) => {
                        const isWithdrawn = c.status === 'withdrawn' || !!c.withdrawnAt;
                        const isExpired = c.status === 'expired';
                        const statusVariant: 'success' | 'critical' | 'warning' | 'glass' = isWithdrawn
                          ? 'critical'
                          : isExpired
                            ? 'warning'
                            : c.status === 'active'
                              ? 'success'
                              : 'glass';
                        return (
                          <div
                            key={c.consentId}
                            className="text-[11px] text-emerald-200/80 flex items-center gap-2 py-1 border-b border-emerald-500/10 last:border-0"
                          >
                            {isWithdrawn ? (
                              <AlertTriangle className="h-3 w-3 shrink-0 text-red-300" />
                            ) : (
                              <CheckCircle2 className="h-3 w-3 shrink-0" />
                            )}
                            <span className="truncate flex-1 min-w-0">
                              <strong>{c.purpose}</strong>
                              {' · '}
                              <Badge variant={statusVariant} className="text-[9px] ml-0.5">
                                {c.status}
                              </Badge>
                              {' · '}
                              <Badge variant="glass" className="text-[9px]">
                                {c.lawfulBasis}
                              </Badge>
                              {c.isMinor && (
                                <Badge variant="warning" className="text-[9px] ml-1">
                                  minor
                                </Badge>
                              )}
                            </span>
                            <span className="text-[10px] opacity-70 shrink-0">
                              {isWithdrawn && c.withdrawnAt
                                ? `withdrawn ${new Date(c.withdrawnAt).toLocaleDateString()}`
                                : `granted ${new Date(c.grantedAt).toLocaleDateString()}`}
                              {!isWithdrawn && c.expiresAt && (
                                <span className="opacity-60">
                                  {' · '}exp {new Date(c.expiresAt).toLocaleDateString()}
                                </span>
                              )}
                            </span>
                            {c.receiptHash && (
                              <code className="font-mono text-[9px] opacity-60 shrink-0">
                                {c.receiptHash.slice(0, 10)}…
                              </code>
                            )}
                          </div>
                        );
                      })}
                      {result.focttaCoreEnrichment.activeConsents.length > 10 && (
                        <div className="text-[10px] text-emerald-200/60 italic mt-1">
                          + {result.focttaCoreEnrichment.activeConsents.length - 10} more
                          consent record{result.focttaCoreEnrichment.activeConsents.length - 10 > 1 ? 's' : ''}
                          {' '}(active + withdrawn + expired)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {result && result.toxicCombinations.length > 0 && (
                <div className="mb-4 rounded-md border border-orange-500/30 bg-orange-500/10 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-orange-200">
                    <AlertTriangle className="h-4 w-4" />
                    Toxic combinations detected
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-orange-200/80">
                    {result.toxicCombinations.map((t) => (
                      <div key={t.assetId} className="flex items-center gap-2">
                        <code className="font-mono text-[11px]">{t.assetId.slice(0, 8)}…</code>
                        <span>has</span>
                        {t.combination.map((l) => (
                          <Badge key={l} variant="glass" className="text-[9px]">{l}</Badge>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {locatedAssets.length > 0 && (
                <>
                  {/* Summary */}
                  <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                    <Badge variant="critical" className="text-[10px]">
                      {locatedAssets.length} location{locatedAssets.length > 1 ? 's' : ''} found
                    </Badge>
                    <span className="text-muted-foreground">across</span>
                    <Badge variant="glass" className="text-[10px]">
                      {new Set(locatedAssets.map((a) => a.sourceType)).size} systems
                    </Badge>
                    <Badge variant="glass" className="text-[10px]">
                      {new Set(locatedAssets.map((a) => a.loc.provider)).size} platforms
                    </Badge>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">
                      {result?.labelLandscape.length ?? 0} <Rx>PII</Rx> types
                    </span>
                  </div>

                  {/* Mini lineage graph: principal -> every asset holding their data */}
                  <div className="rounded-lg border border-border/40 bg-black/20 p-3 mb-4">
                    <div className="flex items-center gap-2 mb-1 text-[11px] text-muted-foreground">
                      <GitBranch className="h-3.5 w-3.5" />
                      Traversal map - where this person's data lives (click a node)
                    </div>
                    <DsarMiniGraph
                      assets={locatedAssets.map((a) => ({ assetId: a.assetId, displayName: a.displayName, sourceType: a.sourceType, sev: a.sev }))}
                      activeId={pickedAsset}
                      onPick={(id) => setPickedAsset((cur) => (cur === id ? null : id))}
                    />
                    <div className="flex items-center gap-3 mt-1 text-[9px] text-muted-foreground">
                      <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SEV_HEX.critical }} />Critical</span>
                      <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SEV_HEX.high }} />High</span>
                      <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SEV_HEX.medium }} />Medium</span>
                      <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: SEV_HEX.low }} />Low</span>
                    </div>
                  </div>

                  {/* Location cards: exactly where the data resides + how to reach it */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-2.5 mb-4">
                    {locatedAssets.map((a, i) => {
                      const active = pickedAsset === a.assetId;
                      const loc = a.loc;
                      return (
                        <motion.div
                          key={a.assetId}
                          initial={{ opacity: 0, y: 6 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(i * 0.03, 0.3) }}
                          onClick={() => setPickedAsset((cur) => (cur === a.assetId ? null : a.assetId))}
                          className={`rounded-lg border p-3 cursor-pointer transition-colors ${active ? 'border-cyan-400/60 bg-cyan-500/5' : 'border-border/40 hover:bg-muted/30'}`}
                        >
                          <div className="flex items-start gap-2">
                            <span className="mt-1 h-2.5 w-2.5 rounded-full shrink-0" style={{ background: SEV_HEX[a.sev], boxShadow: `0 0 8px ${SEV_HEX[a.sev]}` }} />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <code className="font-mono text-xs text-foreground break-all">{a.displayName}</code>
                                {toxicAssetIds.has(a.assetId) && (
                                  <Badge variant="warning" className="text-[8px]">toxic combo</Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-1.5 mt-1.5 text-[11px]">
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold text-[10px]"
                                  style={{ background: `${loc.accent}22`, color: loc.accent, border: `1px solid ${loc.accent}55` }}
                                >
                                  <Cloud className="h-3 w-3" /> {loc.provider}
                                </span>
                                <span className="text-muted-foreground">{loc.service}</span>
                              </div>
                              {(loc.region || loc.regionLabel) && (
                                <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground">
                                  <MapPin className="h-3 w-3" />
                                  {loc.region ?? ''}{loc.regionLabel ? ` · ${loc.regionLabel}` : ''}
                                </div>
                              )}
                              <div className="mt-1.5 space-y-0.5 text-[10px]">
                                {loc.container && (
                                  <div className="flex gap-1.5">
                                    <span className="text-muted-foreground/70 w-16 shrink-0">{loc.containerKind}</span>
                                    <code className="font-mono text-foreground/90 break-all">{loc.container}</code>
                                  </div>
                                )}
                                {loc.path && (
                                  <div className="flex gap-1.5">
                                    <span className="text-muted-foreground/70 w-16 shrink-0">{loc.pathKind}</span>
                                    <code className="font-mono text-foreground/90 break-all">{loc.path}</code>
                                  </div>
                                )}
                              </div>
                              <div className="mt-1.5 flex items-start gap-1 text-[10px] text-muted-foreground/80">
                                <Server className="h-3 w-3 mt-0.5 shrink-0" />
                                <span className="break-all">{loc.reach}</span>
                              </div>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {a.classifications.map((c) => (
                                  <Badge key={c} variant="glass" className="text-[8px]">{c}</Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </>
              )}
              {result && locatedAssets.length === 0 && (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  {/*
                    Was: "the identifier may be unclassified or the asset graph
                    hasn't been scanned yet" - a guess covering three different
                    situations. The server now knows which one it is, so say it.
                  */}
                  {result.resolution?.mode === 'value_matched'
                    ? 'No assets hold this principal. The value index was searched and did not match - this is a genuine "no data" result.'
                    : (result.resolution?.detail ??
                      'No assets matched.')}
                </div>
              )}
              <div className="flex items-center justify-between glass-lite p-3 rounded-md">
                <div>
                  <div className="text-xs font-medium"><Rx>DSAR package ready</Rx></div>
                  <div className="text-[10px] text-muted-foreground">
                    {result?.assets.length ?? 0} assets · {result?.labelLandscape.length ?? 0} <Rx>PII</Rx> labels · Signed JSON + PDF
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    data-action="disc.dspm_reverse_dsar.a02"
                    disabled={exporting || !identifier}
                    onClick={async () => {
                      if (!identifier) return;
                      setExporting(true);
                      try {
                        const exportBody = await exportReverseDsar(identifier.trim(), 'json', inferKind(identifier.trim()));
                        setSignedExport(exportBody);
                        // Trigger browser download of the signed export JSON.
                        const blob = new Blob([JSON.stringify(exportBody, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        // Named by the hash, never the identifier (release 3.1, D1).
                        a.download = `dsar-${exportBody.body.principalIdentifierHash.slice(0, 16)}-${Date.now()}.json`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Export failed');
                      } finally {
                        setExporting(false);
                      }
                    }}
                  >
                    {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    {exporting ? 'Exporting…' : 'Export JSON'}
                  </Button>
                  <Button
                    size="sm"
                    variant="glass"
                    data-action="disc.dspm_reverse_dsar.a03"
                    disabled={pdfBusy || !identifier}
                    onClick={async () => {
                      if (!identifier) return;
                      setPdfBusy(true);
                      setError(null);
                      try {
                        const blob = await exportReverseDsar(identifier.trim(), 'pdf', inferKind(identifier.trim()));
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `dsar-${result?.principalIdentifierHash?.slice(0, 16) ?? 'package'}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'PDF export failed');
                      } finally {
                        setPdfBusy(false);
                      }
                    }}
                  >
                    {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                    {pdfBusy ? 'Rendering…' : 'Download PDF'}
                  </Button>
                </div>
              </div>
              {signedExport?.signature && (
                <div className="mt-3 flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-300 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-emerald-200">Signed &amp; verifiable</div>
                    <div className="text-[10px] text-emerald-200/70 font-mono truncate">
                      {signedExport.signature.algorithm} · keyId {signedExport.signature.keyId} · {new Date(signedExport.signature.signedAt).toLocaleString()}
                    </div>
                  </div>
                  <Badge variant="glass" className="text-[9px] ml-auto shrink-0">HMAC-SHA256</Badge>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
              <GitBranch className="h-16 w-16 opacity-20 mb-4" />
              <p className="text-sm">Enter a principal identifier and click "Run subject data search" to populate.</p>
              <p className="text-xs mt-1">Traversal typically completes in under 8 seconds across all connected sources.</p>
            </div>
          )}
        </GlassCard>
      </div>

      <DsarErasurePanel />
    </PageContainer>
  );
}

/**
 * T3 - DSAR erasure execution. Dry-run by default: preview the rows that
 * would be anonymised; "Execute erasure" SET NULLs the principal's PII
 * columns. Connects to the source with read/write creds (same boundary as
 * the catalog crawl); every run is hash-chained to the audit log.
 */
function DsarErasurePanel() {
  // Release 3.1 persona D1: previewing needs discovery:pd:read (DPO and
  // Compliance); executing, which changes the source database for good, needs
  // discovery:dsar_erase (DPO only). The controller checks both.
  const canPreview = useCan('discovery:pd:read');
  const canExecute = useCan('discovery:dsar_erase');
  const [vendor, setVendor] = useState<'postgres' | 'mssql' | 'oracle'>('postgres');
  const [host, setHost] = useState('postgres');
  const [port, setPort] = useState(5432);
  const [database, setDatabase] = useState('foctta_discovery');
  const [user, setUser] = useState('foctta_app');
  const [password, setPassword] = useState('');
  const [principalValue, setPrincipalValue] = useState('');
  const [schema, setSchema] = useState('crawl_demo');
  const [table, setTable] = useState('t2_valuetest');
  const [matchColumn, setMatchColumn] = useState('email');
  const [eraseColumns, setEraseColumns] = useState('col_a,col_b');
  const [busy, setBusy] = useState<'preview' | 'execute' | null>(null);
  const [summary, setSummary] = useState<DsarEraseSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function run(dryRun: boolean) {
    setBusy(dryRun ? 'preview' : 'execute');
    setErr(null);
    setSummary(null);
    try {
      const res = await runDsarErasure({
        connection: { vendor, host: host.trim(), port: Number(port), database: database.trim(), user: user.trim(), password },
        principalValue: principalValue.trim(),
        targets: [{
          schema: schema.trim(),
          table: table.trim(),
          matchColumn: matchColumn.trim(),
          eraseColumns: eraseColumns.split(',').map((s) => s.trim()).filter(Boolean),
        }],
        dryRun,
      });
      setSummary(res.erasure);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erasure failed');
    } finally {
      setBusy(null);
    }
  }

  const Field = ({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) => (
    <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs font-mono bg-transparent border border-border/40 focus:outline-none focus:border-primary"
      />
    </label>
  );

  return (
    <GlassCard className="p-5 mt-6">
      <CardHeader className="p-0 mb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-warning" /> Right-to-erasure execution (DPDPA §12)
        </CardTitle>
        <CardDescription>
          Anonymises a principal's <Rx>PII</Rx> columns (SET NULL) in the named target. <strong>Dry-run by default</strong> -
          preview the affected row count first, then execute. Every run is hash-chained to the audit log.
        </CardDescription>
      </CardHeader>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Vendor
          <select value={vendor} onChange={(e) => setVendor(e.target.value as typeof vendor)} className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs bg-transparent border border-border/40">
            <option value="postgres">postgres</option>
            <option value="mssql">mssql</option>
            <option value="oracle">oracle</option>
          </select>
        </label>
        <Field label="Host" value={host} onChange={setHost} />
        <Field label="Port" value={String(port)} onChange={(v) => setPort(Number(v) || 0)} />
        <Field label="Database" value={database} onChange={setDatabase} />
        <Field label="User" value={user} onChange={setUser} />
        <Field label="Password" value={password} onChange={setPassword} type="password" />
        <Field label="Principal value" value={principalValue} onChange={setPrincipalValue} />
        <Field label="Match column" value={matchColumn} onChange={setMatchColumn} />
        <Field label="Schema" value={schema} onChange={setSchema} />
        <Field label="Table" value={table} onChange={setTable} />
        <Field label="Erase columns (comma-sep)" value={eraseColumns} onChange={setEraseColumns} />
      </div>
      <div className="flex gap-2">
        {canPreview && (
          <Button data-action="disc.dspm_reverse_dsar.a04" variant="glass" size="sm" onClick={() => void run(true)} disabled={busy !== null || !principalValue.trim() || !password}>
            {busy === 'preview' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Preview (dry-run)
          </Button>
        )}
        {canExecute && (
          <Button data-action="disc.dspm_reverse_dsar.a05" variant="destructive" size="sm" onClick={() => void run(false)} disabled={busy !== null || !principalValue.trim() || !password}>
            {busy === 'execute' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5" />} Execute erasure
          </Button>
        )}
      </div>
      {err && (
        <div className="mt-3 text-xs text-destructive glass-lite border border-destructive/40 rounded-md p-2">{err}</div>
      )}
      {summary && (
        <div className={'mt-3 text-xs glass-lite rounded-md p-3 border ' + (summary.dryRun ? 'border-info/40' : 'border-success/40')}>
          {summary.dryRun ? (
            <>
              <strong>Dry-run preview</strong> - {summary.totalRowsAffected} row(s) would be anonymised.
              {(summary.previews ?? []).map((p, i) => (
                <div key={i} className="mt-1 text-[11px] text-muted-foreground">
                  {p.schema}.{p.table}: <strong>{p.matchedRows}</strong> rows · erase [{p.eraseColumns.join(', ')}]
                </div>
              ))}
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 inline text-success" /> <strong>Erasure executed</strong> - {summary.totalRowsAffected} row(s) anonymised.
              {(summary.results ?? []).map((r, i) => (
                <div key={i} className="mt-1 text-[11px] text-muted-foreground">
                  {r.schema}.{r.table}: <strong>{r.rowsAnonymized}</strong> rows · nulled [{r.eraseColumns.join(', ')}]
                </div>
              ))}
              <div className="mt-1 text-[10px] text-muted-foreground">Audit hash: {summary.principalIdentifierHash.slice(0, 24)}…</div>
            </>
          )}
        </div>
      )}
    </GlassCard>
  );
}
